import { createContext, useContext } from 'react';

/**
 * Toast context + hook (SCRUM-207; title support added by SCRUM-339). Kept
 * separate from the provider component so the file exports only
 * non-components (avoids the react-refresh/only-export-components lint rule).
 */

/**
 * An optional actionable affordance on a toast (SCRUM-794). When present, the toast becomes
 * STICKY — it does not auto-dismiss — so the user has time to act (e.g. Retry a failed persist);
 * clicking the action dismisses the toast. Toasts without an action keep the 4.5s auto-dismiss.
 */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

/** Object form (SCRUM-339): a bold title line above the body, per Ryan's v6 toast. */
export interface ToastInput {
  title?: string;
  body: string;
  variant?: 'default' | 'success';
  /** SCRUM-794: an optional action button; its presence makes the toast sticky. */
  action?: ToastAction;
}

export interface ToastContextValue {
  /**
   * Show a toast. Accepts either the original single-string form
   * (`showToast('Saved', 'success')` — unchanged for existing callers) or the
   * object form with an optional bold title
   * (`showToast({ title: 'Referral sent', body: '…', variant: 'success' })`).
   */
  showToast: (message: string | ToastInput, variant?: 'default' | 'success') => void;
}

export const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}
